import {
  types,
  IModelType,
  getParent,
  // getEnv,
  destroy,
  getSnapshot, applySnapshot
} from 'mobx-state-tree';
import { cloneDeep, assign } from 'lodash';
import { ANDFilter, Sort, AllFilterType } from './filters';
import { FormModel, FormModelProps } from '../components/form/Model';
import { randomUUID } from './uuid';
import { reaction, IReactionDisposer } from 'mobx';
import { Application, Pagination } from 'feathers';
import { get, omit } from 'lodash';

/**
 * Generic List store, able to query and report status of request
 */
export function ListStore<P, A, T>(Model: IModelType<P, A, T>, client?: Application) {
  return types.model(`${Model.name}ListStore`, {
    list: types.optional(types.array(Model), []),
    state: types.optional(types.enumeration('state', ['pending', 'done', 'error']), 'pending'),
    queryFilter: types.optional(ANDFilter, { id: `${Model.name}ListFilters`, filters: {} }),
    parentFilterId: types.maybe(types.string),
    limit: types.optional(types.number, 5),
    querySort: types.optional(Sort, {
      fieldName: 'updated_at',
      ascending: false
    }),

    get listSize() {
      return this.list.length;
    },

    get client(): Application {
      if (client) {
        return client;
      }
      if (getParent(this).client === null || getParent(this).client === undefined) {
        throw new Error(`Parent model does not have client`);
      }
      return getParent(this).client;
    },

    get currentFilters() {
      return this.queryFilter.filters.values().filter(k => k.id !== this.parentFilterId);
    },
  }, {
      refresh(filter?: typeof AllFilterType.SnapshotType, sort?: typeof Sort.Type) {
        if (filter) {
          // tslint:disable-next-line:no-any
          this.parentFilterId = (filter as any).id;
          this.queryFilter.addFilter(
            // tslint:disable-next-line:no-any
            filter as any);
        }
        if (sort) {
          this.querySort = sort;
        }
        return this.executeQuery();
      },

      setLimit(limit: number) {
        this.limit = limit;
        return this.executeQuery();
      },

      addFilter(name: string, value: string, filter: typeof AllFilterType.SnapshotType) {
        const existingFilter = this.queryFilter.filters.get(name);
        if (existingFilter) {
          applySnapshot(existingFilter, filter);
          // existingFilter.setValue(value);
        } else {
          // tslint:disable-next-line:no-any
          this.queryFilter.addFilter(filter as any);
        }
        this.executeQuery();
      },

      removeFilter() {
        const userFilters = this.currentFilters;
        userFilters.forEach(filter => {
          this.queryFilter.filters.delete(filter.id);
        });
        this.executeQuery();
      },

      queryTotal() {
        const query = assign({},
          this.queryFilter.filters.size !== 0 ? this.queryFilter.query : undefined,
          this.querySort.query,
          { $limit: 0 }
        );
        return this.client.service(Model.name).find({ query }).then(r => 
          (r as Pagination<any>).total as number);
      },

      executeQuery() {
        this.state = 'pending';
        const query = assign({},
          this.queryFilter.filters.size !== 0 ? this.queryFilter.query : undefined,
          this.querySort.query,
          { $limit: this.limit }
        );
        return this.client.service(Model.name).find!({ query })
          .then(results => {
            const paginatedResults = results as Pagination<typeof Model.SnapshotType[]>;
            return paginatedResults.data;
          })
          .then(this.listFetchSuccess).catch(this.listFetchError);
      },

      listFetchSuccess(entries: typeof Model.SnapshotType[]) {
        this.list.clear();
        try {
          this.list.push(...entries.map(e => Model.create(e)));
        } catch (e) {
          // tslint:disable-next-line:no-console
          console.error(e.message);
        }
        this.state = 'done';
      },

      listFetchError(error: Error) {
        this.state = 'error';
        // tslint:disable-next-line:no-console
        console.error(`Exception: ${JSON.stringify(error.message)}`);
      }
    }
  );
}

// tslint:disable-next-line:no-any
export function EntityModel<P, A, T>(Model: IModelType<P & { formFields?: any }, A, T>, client?: Application) {
  return types.model(`${Model.name}EntityStore`, {
    state: types.optional(
      types.enumeration('state', ['pending', 'done', 'error']), 'pending'),
    instance: types.maybe(Model),

    get client(): Application {
      if (client) {
        return client;
      }
      if (getParent(this).client === null || getParent(this).client === undefined) {
        throw new Error(`Parent model does not have client`);
      }
      return getParent(this).client;
    },

  }, {
      disposers: () => [] as IReactionDisposer[],
      forms: () => [] as FormModel[]
    }, {

      reset() {
        this.instance = null;
        clearForms(this);
        this.state = 'pending';
      },

      setInstance(instance: typeof Model.SnapshotType) {
        this.instance = Model.create(instance);
        clearForms(this);
        this.initForm();
        this.state = 'done';
      },

      initForm() {
        const formFields = this.instance!.formFields;
        if (formFields) {
          setupModelInputReactions(formFields, formValues => {
            applySnapshot(this.instance!, {
              ...getSnapshot(this.instance!) as object,
              ...formValues
            });
          }, this);
        }
      },

      // tslint:disable-next-line:no-any
      select(id: string) {
        this.state = 'pending';
        const self = this;
        return this.client.service(Model.name).get(id)
          .catch(error =>
            error.code === 404 ?
              this.client.service(Model.name).create!({ _id: id }) :
              Promise.reject(error))
          .then(r => {
            self.setStateToDone(r);
            return r;
          }).catch(error => {
            // tslint:disable-next-line:no-console
            console.error(`error ${error.message}`);
            self.setStateToError();
            return Promise.reject(error);
          });
      },
      setStateToError() {
        this.state = 'error';
      },

      // tslint:disable-next-line:no-any
      setStateToDone(r: any) {
        this.instance = Model.create(r);
        clearForms(this);
        this.initForm();
        this.state = 'done';
      },

      save() {
        if (this.instance !== null && this.instance !== undefined) {
          return (
            this.instance!.formFields ?
              this.forms[0].validate({ showErrors: true }) :
              Promise.resolve({ isValid: true })
            ).then(
              ({ isValid }) => {
                if (isValid) {
                  const sanitized = cloneDeep(getSnapshot(this.instance!));
                  const _id = get(sanitized, '_id') as string | null;
                  return (
                    _id ?
                      this.client.service(Model.name).get(_id)
                        .then(res =>
                          res ?
                            this.client.service(Model.name).update!(_id, sanitized) :
                            this.client.service(Model.name).create!(sanitized)
                        ).catch(error =>
                          error.code === 404 ?
                            this.client.service(Model.name).create!(sanitized) :
                            Promise.reject(error)
                        ) :
                      this.client.service(Model.name).create!(omit(sanitized, '_id'))
                  ).then(res => {
                    applySnapshot(this.instance!, res);
                    return res;
                  });
                } else {
                  return Promise.reject(null);
                }
              }
            );
        }
        return Promise.resolve(null);
      },
    });
}

export const EntityValues = types.model('EntityValues', {
  _id: types.maybe(types.optional(types.string, () => randomUUID())),
  name: types.maybe(types.string),

  get view() {
    return {
      _id: this._id,
      name: this.name
    };
  }
});

export function getFormFields(Model: IModelType<{} & { formFields: FormModelProps }, {}>): FormModelProps {
  const temp = Model.create();
  const formFields = temp.formFields;
  destroy(temp);
  return formFields;
}

export function clearForms(self: { forms: FormModel[], disposers: IReactionDisposer[] }) {
  self.disposers.map(d => d());
  let disposer;
  // tslint:disable-next-line:no-empty
  while (disposer = self.disposers.pop() !== undefined) {}
  let f;
  while (f = self.forms.pop() !== undefined) {}
}

export function setupModelInputReactions(
  // tslint:disable-next-line:no-any
  formFields: FormModelProps,
  onChange: (values: object) => void,
  self: { forms: FormModel[], disposers: IReactionDisposer[] }
) {
  // const tempModel = Model.create();
  const formModel = new FormModel(
    {
      ...formFields,
    });
  // destroy(tempModel);
  self.forms.push(formModel);
  self.disposers.push(
    reaction(
      () => formModel.values(),
      () => onChange(formModel.formValues())
    )
  );
}


// WEBPACK FOOTER //
// ./~/tslint-loader!./src/utils/stores.ts